import {Database, useRpcQuery, useViewQuery} from '@tnc/shared';
import {useEffect, useRef, useState} from 'react';
import {useParams} from 'react-router';

import {profileDetailsTabListTabT} from '@/lib/types/form.types';

import {dateFormatter, usdFormatter} from '@/lib/helpers';
import {SupabaseInstance} from '@/lib/utils';

import {useItemQuery} from '@/hooks';

import {ProfileCardComponent, ProfileDetailsTabListComponent, profileCardSectionT} from '@/components';
import Page from '@/components/Page';
import MarketDepthChart from '@/components/charts/marketDepthChart';
import {DataTableRpc} from '@/components/dataTableRpc';

import PositionDetailProducts from './positionDetailProducts';
import PositionDetailStats from './positionDetailStats';
import PositionDetailTimeline from './positionDetailTimeline';

function PositionDetails() {
  const {id: positionId} = useParams();

  /**
   * Getting position data
   */
  const {data: positionData} = useItemQuery({
    table: 'Position',
    columns: ['*', ['Product', ['*']], ['Brand', ['*']], ['Market', ['*']]],
    id: parseInt(positionId ?? '0'),
    idColumn: 'position_id',
    enabled: !!positionId,
  });

  const {data: timelineData} = useRpcQuery({
    functionName: 'get_position_timeline',
    functionArgs: {__position_id: parseInt(positionId ?? '0')},
    enabled: !!positionId,
  });

  // Get position stats
  const {data: positionStatsData} = useViewQuery({
    view: 'position_detail_stats',
    filters: [{column: 'position_id', operator: 'eq', value: parseInt(positionId ?? '0')}],
    enabled: !!positionId,
  });

  // Calculate progress percentage based on initial quantity vs unfilled items
  const progressPercentage = positionStatsData?.data?.[0]
    ? (((positionData?.position_initial_buy_sell_quantity || 0) - (positionStatsData?.data?.[0]?.unfilled_items || 0)) /
        (positionData?.position_initial_buy_sell_quantity || 1)) *
      100
    : 0;

  // Constants
  const headerInfo = {
    header: 'Position Details',
    subHeader: positionData?.date_created
      ? `Created on ${dateFormatter(positionData?.date_created, 'long', true)}`
      : '',
    render: () => (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-white">Position ID: #{positionId}</h2>
            <div className="flex flex-col justify-start gap-2">
              <span
                className={`inline-flex items-center gap-1 rounded-full text-xs ${
                  positionData?.position_status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}
              >
                <span className="font-bold text-white">Status:</span> {positionData?.position_status || 'Open'}
              </span>
              {positionData && (
                <span
                  className={`inline-flex items-center gap-1 rounded-full text-xs ${
                    positionData?.position_status === 'active'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}
                >
                  <span className="font-bold text-white">Date Created:</span>{' '}
                  {dateFormatter(positionData.date_created, 'short', true)}
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            {positionData?.position_order_type === 'buy' && (
              <div className="text-xs">
                <span className="font-bold text-white">Position Deposit:</span>{' '}
                {usdFormatter.format(positionData?.position_deposit_total || 0)}
              </div>
            )}
            <div className="text-xs">
              <span className="font-bold text-white">Factored Total:</span>{' '}
              {usdFormatter.format(positionData?.position_factored_price_total || 0)}
            </div>
            <div className="text-xs">
              <span className="font-bold text-white">Paid:</span>{' '}
              {positionData?.position_order_type === 'sell'
                ? usdFormatter.format(
                    (positionStatsData?.data?.[0]?.sold_items ?? 0) * (positionData?.position_ask_price ?? 0)
                  )
                : usdFormatter.format(
                    (positionStatsData?.data?.[0]?.purchased_items ?? 0) * (positionData?.position_bid_price ?? 0)
                  )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button className="badge rounded bg-success py-1 text-xs text-white">
            {positionData?.position_order_type.toUpperCase()}{' '}
            {positionData?.position_initial_buy_sell_quantity || 'N/A'} @{' '}
            {usdFormatter.format(
              positionData?.position_order_type === 'buy'
                ? positionData?.position_bid_price || 0
                : positionData?.position_ask_price || 0
            )}
          </button>
          <div className="text-xs">
            <span className="font-bold text-white">Price:</span>{' '}
            {usdFormatter.format(
              positionData?.position_order_type === 'buy'
                ? positionData?.position_bid_price || 0
                : positionData?.position_ask_price || 0
            )}
          </div>
          <div className="text-xs">
            <span className="font-bold text-white">Factored:</span>{' '}
            {usdFormatter.format(positionData?.position_item_unit_factored_cost ?? 0)}
          </div>
        </div>

        <div
          className="progress progress-animate mb-4"
          aria-valuenow={progressPercentage}
          aria-valuemin={0}
          aria-valuemax={100}
          key={Math.random()}
        >
          <div className={`progress-bar bg-success`} style={{width: `${progressPercentage}%`}}></div>
        </div>

        <div className="flex items-center gap-4 text-xs">
          <div>
            <span className="font-bold text-white">Repaid:</span> {Math.round(progressPercentage)}%
          </div>
          <div>
            <span className="font-bold text-white">Total:</span> {positionData?.position_initial_buy_sell_quantity || 0}
          </div>
          <div>
            <span className="font-bold text-white">Filled:</span>{' '}
            {positionData?.position_initial_buy_sell_quantity &&
              positionData?.position_initial_buy_sell_quantity - (positionStatsData?.data?.[0]?.unfilled_items || 0)}
          </div>
          <div className="flex items-center">
            <span className="font-bold text-white">Unfilled:</span> {positionStatsData?.data?.[0]?.unfilled_items || 0}
          </div>
        </div>
      </div>
    ),
  };

  const sections: profileCardSectionT[] = [
    {
      title: 'Brand Info',
      items: [],
      render: () => (
        <div className="flex flex-col space-y-4 rounded-lg text-xs">
          <div className="flex items-center gap-4 pb-2">
            <div className="h-12 w-12 rounded-lg bg-slate-600">
              <img
                src={positionData?.Brand?.brand_logo_url || '/images/logo/logo.png'}
                alt={positionData?.Brand?.brand_name ?? ''}
                className="h-full w-full rounded-lg object-cover"
              />
            </div>
            <div className="flex flex-col">
              <span className="text-gray-400">Brand Name</span>
              <span className="font-semibold">{positionData?.Brand?.brand_name ?? 'N/A'}</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="border-r border-slate-600">
              <div className="text-gray-400">License #</div>
              <div className="flex flex-col">
                <span className="font-semibold">{positionData?.Brand?.brand_license_number ?? 'N/A'}</span>
                <span className="bg-green-500/20 text-green-400 rounded">(active)</span>
              </div>
            </div>
            <div className="border-r border-slate-600">
              <div className="text-gray-400">Volume (30D)</div>
              <div className="flex flex-col">
                <span className="font-semibold">
                  {usdFormatter.format(positionStatsData?.data?.[0]?.brand_monthly_volume ?? 0)}
                </span>
                <span className="text-gray-400">(Monthly Avg)</span>
              </div>
            </div>
            <div className="border-r border-slate-600">
              <div className="text-gray-400">Volume (1D)</div>
              <div className="flex flex-col">
                <span className="font-semibold">
                  {usdFormatter.format(positionStatsData?.data?.[0]?.brand_daily_volume ?? 0)}
                </span>
                <span className="text-gray-400">(Daily Avg)</span>
              </div>
            </div>
          </div>
        </div>
      ),
      className: 'px-6 !py-2',
    },
    {
      title: 'Product Info',
      items: [],
      render: () => (
        <div className="flex flex-col space-y-4 rounded-lg text-xs">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-slate-600">
              <img
                src={positionData?.Product?.product_image_url ?? ''}
                alt={positionData?.Product?.product_name ?? ''}
                className="h-full w-full rounded-lg object-cover"
              />
            </div>
            <div className="flex flex-col">
              <span className="text-gray-400">Product Name</span>
              <span className="font-semibold">{positionData?.Product?.product_name ?? 'N/A'}</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="border-r border-slate-600">
              <div className="text-gray-400">Factored</div>
              <div className="flex flex-col">
                <span className="font-semibold">${25.5}</span>
                <span className="text-gray-400"></span>
              </div>
            </div>
            <div className="border-r border-slate-600">
              <div className="text-gray-400">Volume (30D)</div>
              <div className="flex flex-col">
                <span className="font-semibold">
                  {usdFormatter.format(positionStatsData?.data?.[0]?.product_monthly_volume ?? 0)}
                </span>
                <span className="text-gray-400"></span>
              </div>
            </div>
            <div className="border-r border-slate-600">
              <div className="text-gray-400">Volume (1D)</div>
              <div className="flex flex-col">
                <span className="font-semibold">
                  {usdFormatter.format(positionStatsData?.data?.[0]?.product_daily_volume ?? 0)}
                </span>
                <span className="text-gray-400">(Daily Avg)</span>
              </div>
            </div>
          </div>
        </div>
      ),
      className: 'px-6 !py-2',
    },
    {
      title: 'Market Info',
      items: [],
      render: () => (
        <div className="flex flex-col space-y-4 rounded-lg text-xs">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-slate-600">
              {/* <img
                src={positionData?.Market?. ?? ''}
                alt={positionData?.Market?.market_name ?? ''}
                className="h-full w-full rounded-lg object-cover"
              /> */}
            </div>
            <div className="flex flex-col">
              <span className="text-gray-400">Market Name</span>
              <span className="font-semibold">{positionData?.Market?.market_name ?? 'N/A'}</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="border-r border-slate-600">
              <div className="text-gray-400">Retailers</div>
              <div className="flex flex-col">
                <span className="font-semibold">16</span>
                <span className="text-gray-400"></span>
              </div>
            </div>
            <div className="border-r border-slate-600">
              <div className="text-gray-400">Volume (30D)</div>
              <div className="flex flex-col">
                <span className="font-semibold">
                  {usdFormatter.format(positionStatsData?.data?.[0]?.market_monthly_volume ?? 0)}
                </span>
                <span className="text-gray-400">(Monthly Avg)</span>
              </div>
            </div>
            <div className="border-r border-slate-600">
              <div className="text-gray-400">Volume (1D)</div>
              <div className="flex flex-col">
                <span className="font-semibold">
                  {usdFormatter.format(positionStatsData?.data?.[0]?.market_daily_volume ?? 0)}
                </span>
                <span className="text-gray-400">(Daily Avg)</span>
              </div>
            </div>
          </div>
        </div>
      ),
      className: 'px-6 !py-2',
    },
  ];

  const isMountedRef = useRef(false);
  const [marketDepth, setMarketDepth] = useState<Database['public']['Views']['market_depth_view']['Row'][]>([]);

  useEffect(() => {
    if (!positionData) return;
    const fetchMarketDepth = async () => {
      const query = SupabaseInstance.supabase
        .from('market_depth_view')
        .select('*')
        .eq('fk_product_id', positionData?.fk_product_id ?? 0)
        .eq('fk_market_id', positionData?.fk_market_id ?? 0);

      const {data, error} = await query;

      if (!error && data) {
        setMarketDepth(data);
      }
    };

    // Subscribe to view changes
    const channel = SupabaseInstance.supabase
      .channel(`position_${positionData?.fk_product_id}_${positionData?.fk_market_id}`)
      .on<Database['public']['Tables']['Position']['Row']>(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'Position',
          filter: `fk_product_id=eq.${positionData.fk_product_id}`,
        },
        payload => {
          switch (payload.eventType) {
            case 'INSERT':
            case 'UPDATE':
              if (payload.new.fk_market_id === positionData.fk_market_id) {
                fetchMarketDepth();
              }
              break;
            case 'DELETE':
              if (payload.old.fk_market_id === positionData.fk_market_id) {
                fetchMarketDepth();
              }
              break;
            default:
              throw new Error('Unknown event type');
          }
        }
      )
      .subscribe();

    if (!isMountedRef.current) {
      // Initial fetch
      fetchMarketDepth();
    }

    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      SupabaseInstance.supabase.removeChannel(channel);
    };
  }, [positionData]);

  const tabs: profileDetailsTabListTabT[] = [
    {
      id: 'products',
      title: 'Products',
      icon: 'ri-shopping-cart-2-line',
      content: <PositionDetailProducts />,
    },
  ];

  // Update stats with position data
  const stats = [
    {
      title: 'Position Total',
      value: positionStatsData?.data[0].position_total || 0,
      cardClass: 'bg-success',
      subtitle: 'Paid:',
      subValue: positionStatsData?.data[0].position_paid || 0,
      color: 'white',
    },
    {
      title: 'Product Price',
      value: positionStatsData?.data[0].product_buy_sell_price || 0,
      cardClass: 'bg-success',
      subtitle: 'Factored:',
      subValue: positionStatsData?.data[0].product_factored_price || 0,
      color: 'white',
    },
    {
      title: 'Margin',
      value: positionStatsData?.data[0].margin || 0,
      cardClass: 'bg-success',
      subtitle: 'APR:',
      subValue: positionStatsData?.data[0].margin || 0,
      color: 'white',
    },
    {
      title: 'Factored Margin',
      value: positionStatsData?.data[0].factored_margin || 0,
      cardClass: 'bg-success',
      subtitle: 'Unfilled:',
      subValue: positionStatsData?.data[0].unfilled_items || 0,
      color: 'white',
    },
    {
      title: 'Transfer Totals',
      value: positionStatsData?.data[0].transfer_totals || 0,
      cardClass: 'bg-success',
      subtitle: 'Paid:',
      subValue: positionStatsData?.data[0].transfer_totals || 0,
      color: 'white',
    },
    {
      title: 'Purchased Items',
      value: positionData?.position_order_type === 'buy' ? positionStatsData?.data[0].purchased_items || 0 : 0,
      cardClass: 'bg-success',
      subtitle: 'Unfilled:',
      subValue: positionStatsData?.data[0].unfilled_items || 0,
      color: 'white',
    },
    {
      title: 'Pending Inv.',
      value: positionStatsData?.data[0].pending_inventory || 0,
      cardClass: 'bg-success',
      subtitle: 'Ordered:',
      subValue: positionStatsData?.data[0].pending_inventory || 0,
      color: 'white',
    },
    {
      title: 'Distribution Inv.',
      value: positionStatsData?.data[0].distribution_inventory || 0,
      cardClass: 'bg-success',
      subtitle: 'Distributors:',
      subValue: positionStatsData?.data[0].distribution_inventory || 0,
      color: 'white',
    },
    {
      title: 'Retail Inv.',
      value: positionStatsData?.data[0].retail_inventory || 0,
      cardClass: 'bg-success',
      subtitle: 'Retailers:',
      subValue: positionStatsData?.data[0].retail_inventory || 0,
      color: 'white',
    },
    {
      title: 'Sold Items',
      value: positionStatsData?.data[0].sold_items || 0,
      cardClass: 'bg-success',
      subtitle: 'Paid:',
      subValue: positionStatsData?.data[0].sold_items || 0,
      color: 'white',
    },
  ];

  return (
    <Page currentPage="Sample Position">
      <div className="grid grid-cols-12 gap-x-6">
        <ProfileCardComponent
          className="col-span-12 xl:col-span-5 xxl:col-span-5"
          headerInfo={headerInfo}
          sections={sections}
          headerBg={positionData?.position_status === 'active' ? 'blueGray-700' : 'warning'}
        >
          <PositionDetailTimeline
            title="Position Timeline"
            items={[
              ...(timelineData?.data ?? []).map(
                (item: Database['public']['Functions']['get_position_timeline']['Returns'][0]) => ({
                  date: new Date(item.transaction_date).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  }),
                  time: new Date(item.transaction_date).toLocaleTimeString(undefined, {
                    hour: 'numeric',
                    minute: '2-digit',
                  }),
                  avatar:
                    item.transaction_type === 'transfer' ? (
                      <div className="flex items-center justify-center">
                        <i className="ri-arrow-left-right-line text-2xl"></i>
                      </div>
                    ) : item.transaction_type === 'hold' ? (
                      <div className="flex items-center justify-center">
                        <i className="ri-lock-line text-2xl"></i>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <i className="ri-lock-unlock-line text-2xl"></i>
                      </div>
                    ),
                  name: item.transaction_type,
                  content:
                    item.transaction_type === 'transfer'
                      ? `${item.transfer_direction === 'to' ? '+' : '-'}${item.item_pending_quantity + item.item_retail_quantity + item.item_distributor_quantity || 0} items ${item.sale_price ? `(${item.transfer_direction === 'to' ? '-' : '+'}$${item.sale_price})` : ''}`
                      : item.transaction_type === 'hold'
                        ? 'Funds on Hold'
                        : 'Items released',
                  timestamp:
                    item.transaction_type === 'transfer'
                      ? item.transfer_direction === 'to'
                        ? 'Transfer to position'
                        : 'Transfer from position'
                      : item.transaction_type === 'hold'
                        ? 'Total funds held'
                        : positionData?.position_order_type === 'buy' && item.transfer_direction === 'to'
                          ? 'Transfer to your sell position'
                          : '',
                })
              ),
            ]}
          />
        </ProfileCardComponent>
        <ProfileDetailsTabListComponent
          tabs={tabs}
          // dataItems={processedDataItems}
          lowerContent={
            <DataTableRpc
              functionName="position_order_summary"
              functionArgs={{position_id_input: positionData?.position_id}}
              columns={[
                {
                  field: 'order_group',
                  header: 'Order Group',
                },
                {
                  field: 'order_type',
                  header: 'Order Type',
                },
                {
                  field: 'order_id',
                  header: 'Order ID',
                },
              ]}
              hideAdd
              hideSearch
              customFilterButtons={[]}
              enabled={!!positionData}
            />
          }
          className="col-span-12 xl:col-span-7 xxl:col-span-7"
        >
          <PositionDetailStats stats={stats} />
          <div className="box mt-6 p-4">
            <MarketDepthChart
              data={marketDepth.map(item => ({
                cumulative_quantity: item.cumulative_quantity!,
                order_type: item.position_order_type!,
                price: item.price!,
                quantity: item.quantity!,
              }))}
            />
          </div>
        </ProfileDetailsTabListComponent>
      </div>
    </Page>
  );
}

export default PositionDetails;
